import { useCallback, useEffect, useState } from "react";
import { getReportStrategyPineRequirements } from "../services/reportsApi";
import type { ReportStrategyPineRequirementsResponse } from "../types/reportStrategyPine";

type CacheEntry =
  | { status: "ready"; data: ReportStrategyPineRequirementsResponse }
  | { status: "error"; message: string };

/** Module cache — static catalog; share one GET across all consumers. */
let cache: CacheEntry | null = null;
let inFlight: Promise<CacheEntry> | null = null;

async function loadPineRequirements(force = false): Promise<CacheEntry> {
  if (!force && cache?.status === "ready") return cache;
  if (!force && inFlight) return inFlight;

  inFlight = getReportStrategyPineRequirements()
    .then((data): CacheEntry => {
      cache = { status: "ready", data };
      return cache;
    })
    .catch((e): CacheEntry => {
      const message = e instanceof Error ? e.message : "Failed to load requirements.";
      // Do not sticky-cache errors — next mount / reload can retry.
      cache = null;
      return { status: "error", message };
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function useReportStrategyPineRequirements(): {
  data: ReportStrategyPineRequirementsResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<ReportStrategyPineRequirementsResponse | null>(() =>
    cache?.status === "ready" ? cache.data : null
  );
  const [loading, setLoading] = useState(() => cache?.status !== "ready");
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const force = tick > 0;
    (async () => {
      if (!force && cache?.status === "ready") {
        setData(cache.data);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      const entry = await loadPineRequirements(force);
      if (cancelled) return;
      if (entry.status === "ready") {
        setData(entry.data);
        setError(null);
      } else {
        setData(null);
        setError(entry.message);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const reload = useCallback(() => {
    cache = null;
    setTick((t) => t + 1);
  }, []);

  return {
    data,
    loading,
    error,
    reload,
  };
}
